import type { Update, CreateUpdateData } from '@/types/update'
import { useAuthStore } from '@/stores/auth.store'
import { API_BASE_URL } from './api-config'

const API_URL = API_BASE_URL

class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

async function fetchWithAuth<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const accessToken = useAuthStore.getState().accessToken

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    },
    credentials: 'include',
  })

  const data = await response.json()

  if (!response.ok) {
    throw new ApiError(response.status, data.error || 'Request failed')
  }

  return data
}

export interface UpdatesListParams {
  childId?: string
  type?: string
  before?: string  // Cursor for loading older updates
  since?: string   // Filter updates since this date
  limit?: number
}

export interface UpdatesListResponse {
  updates: Update[]
  hasMore: boolean
  nextCursor: string | null
}

export const updatesApi = {
  async list(params?: UpdatesListParams): Promise<UpdatesListResponse> {
    const searchParams = new URLSearchParams()
    if (params?.childId) searchParams.set('childId', params.childId)
    if (params?.type) searchParams.set('type', params.type)
    if (params?.before) searchParams.set('before', params.before)
    if (params?.since) searchParams.set('since', params.since)
    if (params?.limit) searchParams.set('limit', params.limit.toString())

    const query = searchParams.toString()
    return fetchWithAuth<UpdatesListResponse>(`/api/updates${query ? `?${query}` : ''}`)
  },

  async get(id: string): Promise<{ update: Update }> {
    return fetchWithAuth<{ update: Update }>(`/api/updates/${id}`)
  },

  async create(data: CreateUpdateData): Promise<{ update: Update }> {
    return fetchWithAuth<{ update: Update }>('/api/updates', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async delete(id: string): Promise<{ success: boolean }> {
    return fetchWithAuth<{ success: boolean }>(`/api/updates/${id}`, {
      method: 'DELETE',
    })
  },

  async update(id: string, data: Partial<CreateUpdateData>): Promise<{ update: Update }> {
    return fetchWithAuth<{ update: Update }>(`/api/updates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async search(query: string): Promise<UpdatesListResponse> {
    const searchParams = new URLSearchParams()
    searchParams.set('q', query)
    return fetchWithAuth<UpdatesListResponse>(`/api/updates/search?${searchParams.toString()}`)
  },
}

export { ApiError }
